import jsPDF from 'jspdf';

const strategyMap = {
  conservative: 'Conservative',
  moderate_conservative: 'Moderate Conservative',
  moderate: 'Moderate',
  moderate_aggressive: 'Moderate Aggressive',
  aggressive: 'Aggressive'
};

const vehicleLabels = {
  local_unit_trust: 'Local Unit Trust',
  offshore_unit_trust: 'Offshore Unit Trust',
  retirement_annuity: 'Retirement Annuity',
  tax_free_savings: 'Tax-Free Savings Account',
  preservation_fund: 'Preservation Fund',
  endowment: 'Endowment'
};

const providerLabels = {
  allan_gray: 'Allan Gray',
  ninety_one: 'Ninety One',
  glacier: 'Glacier',
  sygnia: 'Sygnia',
  discovery: 'Discovery',
  sanlam: 'Sanlam',
  liberty: 'Liberty'
};

const profileColors = {
  conservative: [59, 130, 246],
  moderate_conservative: [139, 92, 246],
  moderate: [16, 185, 129],
  moderate_aggressive: [245, 158, 11],
  aggressive: [239, 68, 68]
};

// Helper to normalize old format to new
function normalizeQuotationData(quotationData) {
  if (quotationData.lines && quotationData.lines.length > 0) {
    return quotationData;
  }
  // Old single-vehicle format — migrate
  return {
    investment_providers: quotationData.investment_providers || [],
    risk_profile: quotationData.risk_profile || '',
    lines: [{
      investment_type: quotationData.investment_type || '',
      lump_sum_amount: quotationData.lump_sum_amount || '',
      debit_order_amount: quotationData.debit_order_amount || '',
      debit_order_escalation: quotationData.debit_order_escalation || '',
      upfront_commission: quotationData.upfront_commission || '',
      ongoing_commission: quotationData.ongoing_commission || '',
      currency: quotationData.currency || 'ZAR',
      funds: quotationData.funds || []
    }]
  };
}

export function generateQuotationPDF({ assessment, quotationData, strategies, client }) {
  const data = normalizeQuotationData(quotationData);

  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let y = margin;

  const providers = data.investment_providers || [];
  const riskProfile = data.risk_profile || '';
  const lines = data.lines || [];
  const activeColor = profileColors[riskProfile] || [107, 114, 128];

  const checkPage = (needed) => {
    if (y + needed > pageHeight - 20) {
      doc.addPage();
      y = margin;
    }
  };

  const addFooter = (pageNumber, totalPages) => {
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('RDA Financial Services — Investment Quotation — Confidential', margin, pageHeight - 10);
    doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  };

  const currencySymbol = (curr) => curr === 'USD' ? '$' : curr === 'EUR' ? '€' : 'R';

  // ===== HEADER =====
  doc.setFillColor(30, 58, 138);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Investment Quotation', margin, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('RDA Financial Services', margin, 28);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 35);

  y = 50;

  // ===== SECTION 1: CLIENT DETAILS =====
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('1. Client Details', margin, y);
  y += 2;
  doc.setDrawColor(30, 58, 138);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);

  const clientFields = [
    ['Full Name', client?.full_name || '-'],
    ['Date of Birth', (() => {
      if (!client?.date_of_birth) return '-';
      const dob = new Date(client.date_of_birth);
      const today = new Date();
      let calcAge = today.getFullYear() - dob.getFullYear();
      const md = today.getMonth() - dob.getMonth();
      if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) calcAge--;
      return `${dob.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })} (${calcAge} years)`;
    })()],
    ['Email', client?.email || '-'],
    ['Cellphone', client?.cellphone || '-'],
    ['Assessment Date', assessment?.created_at ? new Date(assessment.created_at).toLocaleDateString('en-ZA') : '-']
  ];

  clientFields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), margin + 45, y);
    y += 6;
  });

  y += 8;

  // ===== SECTION 2: RISK PROFILE =====
  checkPage(30);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('2. Risk Profile & Strategy', margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);

  // Risk profile with color indicator
  doc.setFont('helvetica', 'bold');
  doc.text('Risk Profile:', margin, y);
  doc.setFillColor(...activeColor);
  doc.circle(margin + 40, y - 1, 2, 'F');
  doc.setFont('helvetica', 'normal');
  doc.text(strategyMap[riskProfile] || '-', margin + 45, y);
  y += 7;

  // Strategy name
  const activeStrategy = assessment?.advisor_override && assessment?.override_strategy_id
    ? strategies.find(s => s.id === assessment.override_strategy_id)
    : strategies.find(s => s.risk_profile === riskProfile);
  doc.setFont('helvetica', 'bold');
  doc.text('Strategy:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(activeStrategy?.strategy_name || '-', margin + 45, y);
  y += 10;

  // ===== SECTION 3: INVESTMENT VEHICLES =====
  lines.forEach((line, lineIdx) => {
    const sectionNum = `3${lines.length > 1 ? `.${lineIdx + 1}` : ''}`;
    const vehicleLabel = vehicleLabels[line.investment_type] || 'Not specified';

    checkPage(60);
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum}. ${lines.length > 1 ? `Vehicle ${lineIdx + 1}: ` : ''}${vehicleLabel}`, margin, y);
    y += 2;
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);

    // Investment vehicle type
    doc.setFont('helvetica', 'bold');
    doc.text('Investment Vehicle:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(vehicleLabel, margin + 45, y);
    y += 7;

    // Advisor Commission
    doc.setFont('helvetica', 'bold');
    doc.text('Upfront Commission:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(line.upfront_commission ? `${line.upfront_commission}% ex VAT` : '-', margin + 50, y);
    y += 7;

    doc.setFont('helvetica', 'bold');
    doc.text('Ongoing Commission:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(line.ongoing_commission ? `${line.ongoing_commission}% ex VAT` : '-', margin + 50, y);
    y += 7;

    // Investment Value
    const cs = currencySymbol(line.currency || 'ZAR');
    doc.setFont('helvetica', 'bold');
    doc.text('Lump Sum:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(line.lump_sum_amount ? `${cs} ${Number(line.lump_sum_amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '-', margin + 50, y);
    y += 7;

    doc.setFont('helvetica', 'bold');
    doc.text('Monthly Debit Order:', margin, y);
    doc.setFont('helvetica', 'normal');
    const debitText = line.debit_order_amount
      ? `${cs} ${Number(line.debit_order_amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}${line.debit_order_escalation ? ` (${line.debit_order_escalation}% p.a. escalation)` : ''}`
      : '-';
    doc.text(debitText, margin + 50, y);
    y += 10;

    // Fund Selection table
    const funds = line.funds || [];
    if (funds.length > 0) {
      checkPage(30);

      // Table header
      doc.setFillColor(245, 247, 250);
      doc.rect(margin, y - 4, contentWidth, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text('Fund Name', margin + 5, y);
      doc.text('Allocation', pageWidth - margin - 30, y, { align: 'right' });
      y += 8;

      // Table rows
      doc.setFont('helvetica', 'normal');
      funds.forEach((fund, idx) => {
        checkPage(10);
        if (idx % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(margin, y - 4, contentWidth, 7, 'F');
        }
        doc.setTextColor(60, 60, 60);
        doc.text(fund.name || '-', margin + 5, y);
        doc.setFont('helvetica', 'bold');
        doc.text(`${fund.percentage || 0}%`, pageWidth - margin - 30, y, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        y += 7;
      });

      // Total row
      const fundTotal = funds.reduce((sum, f) => sum + Number(f.percentage || 0), 0);
      y += 2;
      doc.setDrawColor(60, 60, 60);
      doc.setLineWidth(0.3);
      doc.line(margin + 5, y, pageWidth - margin - 5, y);
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('Total', margin + 5, y);
      doc.text(`${fundTotal.toFixed(0)}%`, pageWidth - margin - 30, y, { align: 'right' });
      y += 10;
    }

    y += 5;
  });

  // ===== SECTION 4: PROVIDER SELECTION =====
  checkPage(30);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const providerSectionNumber = 3 + (lines.length > 1 ? lines.length : 0);
  doc.text(`${providerSectionNumber}. Investment Firm Selection`, margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  if (providers.length > 0) {
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    providers.forEach((provider) => {
      checkPage(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`•  ${providerLabels[provider] || provider}`, margin + 5, y);
      y += 7;
    });
  } else {
    doc.setTextColor(150);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text('No providers selected.', margin, y);
  }
  y += 10;

  // ===== SECTION: NEXT STEPS =====
  const nextStepNum = providerSectionNumber + 1;
  checkPage(40);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${nextStepNum}. Next Steps`, margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const nextSteps = [
    'This quotation will be submitted to the administration team for processing.',
    'Quotes will be obtained from the selected investment firm(s) listed above.',
    'The advisor will present the final quote(s) to the client for approval.',
    'Upon client acceptance, the investment will be initiated as per the selected vehicle and fund allocation.'
  ];

  nextSteps.forEach((text) => {
    checkPage(12);
    const textLines = doc.splitTextToSize(text, contentWidth - 5);
    doc.text(textLines, margin, y);
    y += textLines.length * 5 + 3;
  });

  y += 10;

  // Signature lines
  checkPage(30);
  doc.line(margin, y, margin + 70, y);
  doc.line(pageWidth - margin - 70, y, pageWidth - margin, y);
  y += 5;
  doc.setFontSize(10);
  doc.text('Advisor Signature', margin, y);
  doc.text('Date', pageWidth - margin - 70, y);
  y += 12;
  doc.line(margin, y, margin + 70, y);
  doc.line(pageWidth - margin - 70, y, pageWidth - margin, y);
  y += 5;
  doc.text('Client Signature', margin, y);
  doc.text('Date', pageWidth - margin - 70, y);

  // Add footers to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  // Save
  const filename = `Quotation_${(client?.full_name || 'Client').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}
